import { ChannelGroupService } from '@service/channel/channel-group.service';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ChannelService } from '@service/channel/channel.service';
import { Channel } from 'icreate/common/models/channel';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { ChannelGroup } from 'icreate/common/models/channel-group';
import { lastValueFrom } from 'rxjs';
import { ChannelCategoryService } from '@service/channel/channel-category.service';
import { ChannelType, channelTypesSelector, isBlank } from 'icreate/common/models/channel-type';
import { ListBubbleSelectorComponent } from '@components/list-bubble-selector/list-bubble-selector.component';
import { TranslateService } from '@ngx-translate/core';
import { AutosaveControlService, AutosavePage } from '@service/auto-save-controller.service';

export interface ChannelSearchDialogData {
    vesselId: string;
    assignedChannel?: Channel | null;
    allowedTypesSet?: Set<ChannelType>;
    setShouldDisableTypes?: boolean;
    multipleSelection?: boolean;
    preSelectedChannelsCount?: number;
    has2Steps?: boolean;
    stopAutosave?: boolean;
    autoSavePage?: AutosavePage;
}

@Component({
    selector: 'app-channel-search-dialog',
    templateUrl: './channel-search-dialog.component.html',
    styleUrls: ['./channel-search-dialog.component.css'],
    standalone: false,
})
export class ChannelSearchDialogComponent implements OnInit, OnDestroy {
    @ViewChild('categoriesSelector')
    categoriesSelector: ListBubbleSelectorComponent;

    @ViewChild('groupsSelector')
    groupsSelector: ListBubbleSelectorComponent;

    selectedChannels: Channel[] = [];
    allowedTypesSet: Set<ChannelType>;

    channels: Channel[];
    channelGroups: ChannelGroup[];
    channelCategories: ChannelCategory[];

    selectedTypes: string[] = [];

    selectedGroups: ChannelGroup[] = [];
    selectedCategories: ChannelCategory[] = [];

    filteredChannelList: Channel[] = [];
    filteredGroupList: ChannelGroup[] = [];
    filteredTypesList: string[] = [];

    inputChannel = '';

    isLoadingChannels = false;
    shouldDisableTypes = false;
    multipleSelection = false;
    has2Steps = false;

    originalChannel: Channel | undefined;

    preSelectedChannelsCount = 0;

    autoSavePage: AutosavePage;
    stopAutoSave = false;

    constructor(
        private dialogRef: MatDialogRef<ChannelSearchDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ChannelSearchDialogData,
        private channelService: ChannelService,
        private channelGroupService: ChannelGroupService,
        private channelCategoryService: ChannelCategoryService,
        private translateService: TranslateService,
        private autosaveControlService: AutosaveControlService
    ) {}

    async ngOnInit() {
        this.selectedChannels = this.data.assignedChannel ? [this.data.assignedChannel] : [];

        this.multipleSelection = this.data.multipleSelection ?? false;
        this.preSelectedChannelsCount = this.data.preSelectedChannelsCount ?? 0;
        this.has2Steps = this.data.has2Steps ?? false;

        this.stopAutoSave = this.data.stopAutosave ?? false;
        this.autoSavePage = this.data.autoSavePage || undefined;

        if (this.data.setShouldDisableTypes) {
            this.shouldDisableTypes = !!this.data.allowedTypesSet;
        }

        if (this.stopAutoSave && this.autoSavePage) {
            this.autosaveControlService.setDialogOpen(this.autoSavePage, true);
        }
        this.allowedTypesSet = this.data.allowedTypesSet ?? new Set();
        await this.loadChannels();
    }

    private async loadChannels() {
        if (!this.data.vesselId) return;

        this.isLoadingChannels = true;
        this.channels = undefined;

        const vesselChannels = await lastValueFrom(this.channelService.getChannels(this.data.vesselId));

        if (vesselChannels) {
            this.originalChannel = structuredClone(this.data.assignedChannel);

            this.channels = vesselChannels;

            this.channelGroups = await this.channelGroupService.getList(this.data.vesselId);

            this.channelCategories = await this.channelCategoryService.getList(this.data.vesselId);

            this.updateFilteredCategories();
            this.updateFilteredGroups();

            this.prepareChannelTypesList();
            this.updateTypesSet();
        }

        this.isLoadingChannels = false;
    }

    ngOnDestroy(): void {
        if (this.stopAutoSave && this.autoSavePage) {
            this.autosaveControlService.setDialogOpen(this.autoSavePage, false);
        }
    }

    updateTypesSet(selectedTypes?: string[]) {
        if (!this.shouldDisableTypes) {
            this.allowedTypesSet = new Set();

            if (selectedTypes?.length > 0) {
                channelTypesSelector.forEach(type => {
                    const translatedName: string = this.translateService.instant(type.name);

                    if (selectedTypes.includes(translatedName)) {
                        this.allowedTypesSet = new Set([...this.allowedTypesSet, ...type.allowedChannels]);
                    }
                });
            } else {
                channelTypesSelector.forEach(type => {
                    this.allowedTypesSet = new Set([...this.allowedTypesSet, ...type.allowedChannels]);
                });
            }
        }

        this.searchChannels();
    }

    updateFilteredCategories(selectedCategories?: ChannelCategory[]) {
        if (selectedCategories) {
            this.selectedCategories = selectedCategories;
        }

        if (this.selectedCategories.length === 0) {
            this.resetGroups();
            return;
        }

        const selectedCategoriesIds = new Set(this.selectedCategories.map(category => category.id));

        this.filteredGroupList = this.channelGroups.filter(group => selectedCategoriesIds.has(group.categoryId));

        this.selectedGroups = this.selectedGroups.filter(group => selectedCategoriesIds.has(group.categoryId));

        this.groupsSelector?.clearSelectedElements?.();

        this.selectedGroups.forEach(group => {
            this.groupsSelector?.selectElement?.(group);
        });

        this.searchChannels();
    }

    updateFilteredGroups(selectedGroups?: ChannelGroup[]) {
        if (selectedGroups) {
            this.selectedGroups = selectedGroups;
        }

        this.searchChannels();
    }

    searchChannels() {
        this.filteredChannelList = [];

        const selectedGroupsId: Set<any> = new Set();

        this.selectedGroups.forEach(group => selectedGroupsId.add(group.id));

        const selectedCategoriesId: Set<any> = new Set();

        this.selectedCategories.forEach(category => selectedCategoriesId.add(category.id));

        const groupsCategoriesMap = new Map();

        this.channelGroups.forEach(group => {
            if (selectedCategoriesId.size > 0) {
                if (selectedCategoriesId.has(group.categoryId)) {
                    groupsCategoriesMap.set(group.id, group.categoryId);
                }
            } else {
                groupsCategoriesMap.set(group.id, group.categoryId);
            }
        });

        const inputChannel = String(this.inputChannel ?? '')
            .trim()
            .toLowerCase();

        const onlyCategorySelectedFilter =
            this.selectedTypes.length === 0 && selectedCategoriesId.size > 0 && selectedGroupsId.size === 0;

        const onlyTypeSelectedFilter =
            this.selectedTypes.length > 0 && selectedCategoriesId.size === 0 && selectedGroupsId.size === 0;

        const hasInput = this.inputChannel.length > 0;

        if (hasInput || (!onlyCategorySelectedFilter && !onlyTypeSelectedFilter)) {
            this.filteredChannelList = this.channels?.filter((c: Channel) => {
                const nameMatches = c.opsData?.name?.trim().toLowerCase().includes(inputChannel);

                const tagMatches = c.opsData?.tag?.trim().toLowerCase().includes(inputChannel);

                const isChannelTypeValid = !isBlank(c.opsData?.channelType);

                const isNameOrTagMatch = nameMatches || tagMatches;

                const isTypeAllowed =
                    this.allowedTypesSet?.size === 0 || this.allowedTypesSet?.has(c.fcuData.channelType);

                const isGroupMatched =
                    selectedGroupsId.size === 0
                        ? groupsCategoriesMap.has(c.groupId)
                        : selectedGroupsId.has(c.groupId) && groupsCategoriesMap.has(c.groupId);

                const hasGroupOrCategoryFilter = selectedGroupsId.size > 0 || selectedCategoriesId.size > 0;

                if (!isTypeAllowed) {
                    return false;
                }

                if (hasInput && isChannelTypeValid && isNameOrTagMatch) {
                    if (this.allowedTypesSet?.size > 0 || hasGroupOrCategoryFilter) {
                        return isTypeAllowed && isGroupMatched;
                    }

                    return true;
                }

                if (!hasInput && !hasGroupOrCategoryFilter) {
                    return false;
                }

                if (isGroupMatched) {
                    const checked = this.selectedChannels.some(channel => channel.id == c.id);

                    if (!hasInput || (hasInput && isNameOrTagMatch)) {
                        c.checked = checked;
                        return true;
                    }
                }

                return false;
            });
        }

        if (
            this.filteredChannelList?.length === 0 &&
            this.selectedChannels.length > 0 &&
            this.selectedCategories?.length === 0 &&
            this.selectedGroups?.length === 0 &&
            !this.inputChannel
        ) {
            const channels = structuredClone(this.selectedChannels);

            this.filteredChannelList = channels.map(channel => {
                channel.checked = true;
                return channel;
            });
        }
    }

    handleChannelSelected(channels: Channel[]) {
        this.selectedChannels = channels;
    }

    cancel() {
        this.dialogRef.close();
    }

    reset() {
        this.clearDialog();
        this.updateFilteredGroups();
        this.updateFilteredCategories();
    }

    clearDialog() {
        this.selectedGroups = [];
        this.selectedCategories = [];

        if (!this.shouldDisableTypes) {
            this.selectedTypes = [];
        }

        this.filteredChannelList = [];
        this.filteredGroupList = [];
        this.inputChannel = '';

        this.groupsSelector?.clearSelectedElements();
        this.categoriesSelector?.clearSelectedElements();
    }

    resetGroups() {
        this.selectedGroups = [];
        this.filteredChannelList = [];
        this.filteredGroupList = [];

        this.groupsSelector?.clearSelectedElements();

        this.searchChannels();
    }

    selectChannel() {
        if (this.selectedChannels.length === 0) return;
        if (!this.multipleSelection && this.data.assignedChannel?.id === this.selectedChannels[0]?.id) {
            return;
        }

        this.shouldDisableTypes = false;

        const selection = this.multipleSelection ? this.selectedChannels : this.selectedChannels[0];
        this.dialogRef.close(selection);
    }

    prepareChannelTypesList() {
        this.filteredTypesList = [];
        this.selectedTypes = [];

        channelTypesSelector.forEach(type => {
            const translatedName = this.translateService.instant(type.name);

            this.filteredTypesList.push(translatedName);

            if (this.shouldDisableTypes && this.allowedTypesSet) {
                const allowedTypes = [...type.allowedChannels];

                if (allowedTypes.some(value => this.allowedTypesSet.has(value))) {
                    this.selectedTypes.push(translatedName);
                }
            }
        });
    }
}
